import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import SkeletonGroup from 'components/Skeleton/SkeletonGroup';

import useCommentIdFromRouterHash from 'hooks/useCommentIdFromRouterHash';
import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useMediaQuery from 'hooks/useMediaQuery';
import useRatings from 'hooks/useRatings';

import { nonNull, stripObjDuplicates } from 'lib/array';
import { getCommentsCount } from 'lib/comments';
import { sendEvent } from 'lib/gtag';
import { commentQueryKeys, reviewQueryKeys } from 'lib/queryKeys';
import {
  formatReviewsAsComments,
  removeReviewsFromComments,
} from 'lib/reviews';
import { scrollToElement } from 'lib/utils';

import { useDomain } from 'providers/DomainProvider';

import { getAliasReviewsList } from 'services/alias';

import { useCommentsRoot } from '../Root';

import COMMENT from 'constants/comment';
import OFFER from 'constants/offer';

const CommentListItem = dynamic(() => import('./ListItem'));

const CommentListItemSkeleton = dynamic(() => import('./ListItem/Skeleton'));

const COMMENTS_STALE_TIME = 20_000; // 20 seconds
const COMMENTS_LIST_HEADER_HEIGHT = 132;
const MILLISECONDS_TO_WAIT_COMMENTS_LIST_BE_MOUNTED = 800;
const REVIEWS_LIMIT = 15;

const uniq = (
  comments,
  uniqueComments = [],
  discoveredCommentsByIds = new Map()
) => {
  for (const comment of comments) {
    const discoveredComment = discoveredCommentsByIds.get(comment.commentId);

    if (discoveredComment) {
      if (discoveredComment.replies?.length > 0) {
        comment.replies.push(
          ...uniq(comment.replies, [], discoveredCommentsByIds)
        );
      }
      continue;
    }

    uniqueComments.push(comment);
    discoveredCommentsByIds.set(comment.commentId, comment);

    if (comment.replies?.length > 0) {
      comment.replies = uniq(comment.replies, [], discoveredCommentsByIds);
    }
  }

  return uniqueComments;
};

const CommentsList = ({
  aliasId = null,
  initialComments,
  canCommentsBeRecommendedAsReview = false,
  commentsCount: initialCommentsCount,
  className,
  queryFn,
}) => {
  const hashCommentRef = useRef(null);
  const commentsListComponentRef = useRef(null);
  const lastCommentIdFromUrlHash = useRef(null);
  const fetchNextPageRef = useRef(null);
  const { isLg } = useMediaQuery();
  const { commentId: commentIdFromUrlHash } = useCommentIdFromRouterHash();
  const { domain, domainId, domainIdKey } = useDomain();
  const { isLiked, isDisliked } = useRatings({ domain, domainId });
  const {
    commentsSort,
    gaEventCategory,
    dispatchCommentsCount,
    setCommentsListEmpty,
  } = useCommentsRoot();

  const {
    data: comments,
    fetchNextPage: fetchNextCommentsPage,
    isLoading: isCommentsLoading,
    isSuccess: isCommentsSuccess,
    isFetchingNextPage: isFetchingNextCommentsPage,
    isInitialLoading: isInitialCommentsLoading,
    hasNextPage: hasNextCommentsPage,
  } = useInfiniteQuery({
    queryKey: commentQueryKeys[domain].list({
      [domainIdKey]: domainId,
      sortType: commentsSort,
    }),
    queryFn: async (after, { signal }) =>
      queryFn(
        {
          after,
          comment: commentIdFromUrlHash,
          limit: COMMENT.LIMIT,
          repliesLimit: COMMENT.REPLIES_LIMIT,
          sort: commentsSort,
          [domainIdKey]: domainId,
        },
        { signal }
      ),
    initialData: initialComments,
    /**
     * The API takes at max 20 seconds to add a new comment to the actual
     * list, so if a user creates a new comment and tries to reload the
     * comments, it won't appear. We keep this list on cache or 20 seconds
     * so the user still sees newly added comments until the API does the
     * processing needed to actually return it on the GET comments list
     */
    staleTime: Infinity,
    cacheTime: COMMENTS_STALE_TIME,
    useErrorBoundary: true,
    getNextPageParam: (lastComments) =>
      lastComments?.length >= COMMENT.LIMIT
        ? lastComments?.at(-1)?.commentId
        : null,
    /**
     * In specific cases, the `commentPages` can be transform as a array of
     * null, because this, we filter the `commentPages` before treat the comment
     * duplicates.
     *
     * There are edge cases where comments can be duplicated from the APIs..
     * here we make sure those won't break the list and treat comments as
     * a flat list instead of dealing with an array of pages for easier
     * rendering.
     */
    select: (pages) =>
      /**
       * Since we are appending reviews in the list, we need to purge received
       * reviews from API to prevent duplicated key errors in production.
       */
      removeReviewsFromComments(
        stripObjDuplicates(nonNull(pages), 'commentId')
      ),
    onSuccess: (data) => {
      dispatchCommentsCount({
        type: 'populate',
        count: getCommentsCount(
          initialCommentsCount,
          // TODO: don't know why always the first item is null when page reload
          data.pages.flat().filter(Boolean)
        ),
      });
    },
  });

  const isReviewsInfiniteQueryEnabled =
    !hasNextCommentsPage && domain === OFFER.DOMAIN && !!aliasId;

  /**
   * We are making a temporarily test to know if injecting reviews inside the
   * comments list improves the user interactions in the offer. Unfortunately to
   * make it was necessary modify several parts of components that composes the
   * comments list, which should be more generic as possible to be used in
   * many different verticals.
   */
  const {
    data: reviews,
    fetchNextPage: fetchNextReviewsPage,
    hasNextPage: hasNextReviewsPage,
    isLoading: isReviewsLoading,
    isInitialLoading: isInitialReviewsLoading,
    isSuccess: isReviewsSuccess,
    isFetchingNextPage: isFetchingNextReviewsPage,
  } = useInfiniteQuery({
    queryKey: reviewQueryKeys.list({ aliasId, sort: COMMENT.REVIEWS_SORT }),
    queryFn: (after, { signal }) =>
      getAliasReviewsList(
        {
          after,
          aliasId,
          limit: REVIEWS_LIMIT,
        },
        { signal }
      ),
    // Optional-chaining to avoid possible errors when a refetching is
    // triggered due a like dispatching.
    getNextPageParam: (lastReviews) => lastReviews?.meta?.pagination.after,
    select: (pages) => {
      if (!pages) {
        return [];
      }

      const reviewsWithStars = pages.map((review) => ({
        ...review,
        stars: review.stars || OFFER.REVIEWS.DEFAULT_RATING_VALUE,
      }));

      return formatReviewsAsComments(reviewsWithStars, aliasId);
    },
    flattenKey: 'data',
    refetchOnMount: false,
    enabled: isReviewsInfiniteQueryEnabled,
  });

  const isLoading =
    isCommentsLoading || (isReviewsInfiniteQueryEnabled && isReviewsLoading);
  const isSuccess = isCommentsSuccess || isReviewsSuccess;
  const isFetchingNextPage =
    isFetchingNextCommentsPage || isFetchingNextReviewsPage;
  const isInitialLoading = isInitialCommentsLoading || isInitialReviewsLoading;

  useEffect(() => {
    let timeout;

    if (lastCommentIdFromUrlHash.current !== commentIdFromUrlHash) {
      timeout = setTimeout(() => {
        if (!isSuccess || !hashCommentRef.current) {
          return;
        }

        if (!isLg) {
          scrollToElement(hashCommentRef.current.offsetTop);
          lastCommentIdFromUrlHash.current = commentIdFromUrlHash;
          return;
        }

        commentsListComponentRef.current.scroll({
          top: hashCommentRef.current.offsetTop - COMMENTS_LIST_HEADER_HEIGHT,
          left: 0,
          behavior: 'smooth',
        });
        lastCommentIdFromUrlHash.current = commentIdFromUrlHash;
      }, MILLISECONDS_TO_WAIT_COMMENTS_LIST_BE_MOUNTED);
    }

    return () => clearTimeout(timeout);
  }, [isSuccess, commentIdFromUrlHash, isLg]);

  useIntersectionObserver({
    enabled: hasNextCommentsPage,
    target: fetchNextPageRef,
    onIntersect: () => {
      sendEvent({
        action: 'load_comments_reading',
        category: gaEventCategory,
      });
      fetchNextCommentsPage();
    },
  });

  useIntersectionObserver({
    enabled: isReviewsInfiniteQueryEnabled && hasNextReviewsPage,
    target: fetchNextPageRef,
    onIntersect: () => {
      sendEvent({
        action: 'load_reviews_reading',
        category: gaEventCategory,
      });
      fetchNextReviewsPage();
    },
  });

  const commentsWithReviews = useMemo(() => {
    if (!isReviewsInfiniteQueryEnabled) {
      return comments;
    }

    return uniq([...comments, ...reviews]);
  }, [isReviewsInfiniteQueryEnabled, comments, reviews]);

  useEffect(() => {
    if (!isInitialLoading && isSuccess && commentsWithReviews.length === 0) {
      setCommentsListEmpty(true);
      return;
    }

    setCommentsListEmpty(false);
  }, [isInitialLoading, isSuccess, commentsWithReviews]);

  if (isSuccess && commentsWithReviews.length === 0) {
    return null;
  }

  return (
    <div
      ref={commentsListComponentRef}
      className={twMerge(
        'scrollbar-light flex flex-col gap-y-2 overflow-y-auto overflow-x-hidden pr-1 lg:pb-6',
        className
      )}
    >
      {commentsWithReviews.map(
        ({
          commentDate,
          commentFixed,
          commentId,
          commentLikes,
          commentStatusName,
          commentText,
          edited,
          isReview,
          parentId,
          replies,
          userId,
          userLevel,
          userName,
          userPhoto,
          userSpecialtyName,
          userTypeName,
          userUsername,
        }) => (
          <CommentListItem
            key={commentId}
            aliasId={aliasId}
            canBeRecommendedAsReview={canCommentsBeRecommendedAsReview}
            commentDate={commentDate}
            commentFixed={commentFixed}
            commentId={commentId}
            commentLikes={commentLikes}
            commentStatusName={commentStatusName}
            commentText={commentText}
            hashCommentRef={hashCommentRef}
            isDisliked={isDisliked(commentId)}
            isEdited={edited}
            isLiked={isLiked(commentId)}
            isReview={isReview}
            parentId={parentId}
            replies={replies}
            userId={userId}
            userLevel={userLevel}
            userName={userName}
            userPhoto={userPhoto}
            userSpecialtyName={userSpecialtyName}
            userTypeName={userTypeName}
            userUsername={userUsername}
          />
        )
      )}
      {(isLoading || isFetchingNextPage || isInitialLoading) && (
        <SkeletonGroup items={2} skeleton={CommentListItemSkeleton} />
      )}
      <div ref={fetchNextPageRef} />
    </div>
  );
};

CommentsList.propTypes = {
  aliasId: PropTypes.number,
  initialComments: PropTypes.arrayOf(
    PropTypes.shape({
      canBeRecommendedAsReview: PropTypes.bool,
      commentDate: PropTypes.string.isRequired,
      commentFixed: PropTypes.bool,
      commentId: PropTypes.number.isRequired,
      commentLikes: PropTypes.number.isRequired,
      commentStatusName: PropTypes.string.isRequired,
      commentText: PropTypes.string.isRequired,
      edited: PropTypes.bool.isRequired,
      isReply: PropTypes.bool,
      isReview: PropTypes.bool,
      parentId: PropTypes.number,
      replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
      userId: PropTypes.number.isRequired,
      userLevel: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userSpecialtyName: PropTypes.string,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
    })
  ),
  canCommentsBeRecommendedAsReview: PropTypes.bool,
  commentsCount: PropTypes.number.isRequired,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  queryFn: PropTypes.func.isRequired,
};

export default CommentsList;
